import { useState } from 'react'
import { useForm } from 'react-hook-form'

import Button from 'src/ui/atoms/Button'
import Modal from 'src/ui/atoms/Modal'
import WrappedInput from 'src/ui/atoms/Input/Wrapped'
import WrappedSelect from 'src/ui/atoms/Select/Wrapped'
import { checkEthAddress } from 'src/util/fieldValidations'
import useNotificationsApi from './useNotificationsApi'

const CreateNotification = ({ onSuccess }: { onSuccess: Function }) => {
  const [modal, setModal] = useState(false)
  const { createNotification } = useNotificationsApi()

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isValid, isSubmitting },
  } = useForm({
    mode: 'onChange',
  })

  const handleCreateNotification = async ({
    name,
    tokenAddress,
    toOrFrom,
    telegramChatId,
    email,
  }: {
    name: string
    tokenAddress: string
    toOrFrom: string
    telegramChatId: string
    email: string
  }) => {
    await createNotification({
      name,
      tokenAddress,
      toOrFrom,
      telegramChatId,
      email,
    })
    setModal(false)
    reset()
    onSuccess()
  }

  const type = watch('type')
  const telegramLink = (
    <a
      href="https://t.me/zoidcore_bot"
      target="_blank"
      className="cursor-pointer text-white hover:text-main-blue"
      rel="noreferrer">
      Contact ZoidCore bot
    </a>
  )

  return (
    <div>
      <Button
        size="M"
        name="New"
        onClick={() => {
          setModal(true)
        }}
      />
      {modal && (
        <Modal title="New notification (Ethereum Mainnet only)">
          <form
            className="flex flex-col gap-4"
            onSubmit={handleSubmit((data) => handleCreateNotification(data as any))}>
            <WrappedInput
              placeholder="Name"
              label="Name"
              error={errors.name}
              {...register('name', {
                required: 'Required field.',
              })}
            />
            <WrappedInput
              placeholder="Token address"
              label="Token address"
              error={errors.tokenAddress}
              {...register('tokenAddress', {
                required: 'Required field.',
                validate: (val: string) => {
                  return checkEthAddress(val) ? true : 'The address does not have a correct format'
                },
              })}
            />
            <WrappedInput
              placeholder="Address to watch"
              label="Address to watch"
              error={errors.toOrFrom}
              {...register('toOrFrom', {
                required: 'Required field.',
                validate: (val: string) => {
                  return checkEthAddress(val) ? true : 'The address does not have a correct format'
                },
              })}
            />
            <WrappedSelect
              placeholder="Type"
              label="Type"
              options={[
                {
                  text: 'Telegram',
                  value: 'telegram',
                },
                {
                  text: 'Email',
                  value: 'email',
                },
              ]}
              {...register('type', {
                required: 'Required field.',
              })}
            />
            {type === 'email' && (
              <WrappedInput
                placeholder="your_email@mail.com"
                label="Email"
                error={errors.email}
                {...register('email', {
                  required: 'Required field.',
                  pattern: {
                    value: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                    message: 'Incorrect email',
                  },
                })}
              />
            )}
            {type === 'telegram' && (
              <WrappedInput
                placeholder="Chat ID"
                label="Chat Id"
                error={errors.telegramChatId}
                {...register('telegramChatId', {
                  required: 'Required field.',
                })}>
                {telegramLink}
              </WrappedInput>
            )}

            <div className="flex justify-end gap-4">
              <Button size="M" name="Cancel" type="secondary" onClick={() => setModal(false)} />
              <Button size="M" name="Create" disabled={!isValid} loading={isSubmitting} />
            </div>
          </form>
        </Modal>
      )}
    </div>
  )
}

export default CreateNotification
